/**
 * DUE-196: the single inventory of every key this client persists.
 *
 * Kept in a dependency-free leaf module on purpose: `lib/clientData.ts`
 * (the wipe), `lib/storageMigration.ts` (the one-shot cloud purge) and each
 * owning module all import from here, so adding a new persisted key has
 * exactly one place where it must be declared — and therefore exactly one
 * place where "is this wiped on logout / account deletion?" is answered.
 *
 * Storage channel per key is documented next to it. "device" = the
 * `Platform.storage` seam (localStorage in Telegram/web, Capacitor
 * Preferences on iOS); "keychain" = `Platform.secureStorage` (Keychain on
 * iOS, device storage elsewhere); "cloud" = Telegram CloudStorage, i.e.
 * Telegram's own servers — only ever acceptable for non-personal data.
 */

/** device. Onboarding wizard draft: name, birth date/time, city + coords. */
export const ONBOARDING_DRAFT_KEY = "onboarding_draft";

/** browser localStorage. Partner birth data for the synastry wizard. */
export const PARTNER_DRAFT_KEY = "synastry_partner_draft";

/** device. Session envelope: access JWT + user profile. */
export const SESSION_KEY = "duetto.session";

/** keychain. Long-lived refresh token (iOS device/Apple auth). */
export const REFRESH_TOKEN_KEY = "duetto.refresh_token";

/** keychain. iOS guest-account identifier. Survives logout, not deletion. */
export const DEVICE_ID_KEY = "duetto.device_id";

/**
 * device. design_v2 theme override: `auto` (default, by device local time),
 * `sunset` or `dawn`. Deliberately NOT wiped by `clearClientData` — it is a
 * property of this device's display, not of the person signed in on it.
 * E5 adds the account-level column; until then this is the whole truth.
 */
export const THEME_PREFERENCE_KEY = "duetto.theme_preference";

/**
 * device. DUE-471: the usage-analytics opt-out, `"true"` (default when
 * absent) or `"false"`. Read by `lib/analytics.ts`, written by the Settings
 * switch. Deliberately NOT wiped by `clearClientData` — like the theme, it is
 * a property of this device, and a sign-out must not silently turn tracking
 * back on.
 */
export const ANALYTICS_ENABLED_KEY = "duetto.analytics_enabled";

/** device. Last APNs token handed to the backend (iOS only). */
export const PUSH_TOKEN_KEY = "ios_push_token";

/**
 * device. DUE-211 task 14: marks that this device's push-notification
 * permission question has been *answered* — by name, not by where it was
 * asked. Set by `enableIosPush()` / `disableIosPush()`
 * (`lib/platform/pushRegistration.ts`) on every call, regardless of
 * caller: the Profile toggle (on *or* off) and the natal-reading
 * Today ask (`requestDailyPushPrompt`) both go through those two
 * functions. Read by `requestDailyPushPrompt()` to decide whether
 * to ask at all.
 *
 * This used to be scoped to just the natal-reading prompt
 * (`daily_transits_enabled` alone gated re-asking), which let a user's
 * explicit "off" on the Profile toggle get silently reversed: iOS
 * remembers the OS-level grant from an earlier "on", so a later
 * auto-prompt on a device that had never opened Today yet saw
 * `daily_transits_enabled: false`, called `enableIosPush()`, got
 * `"registered"` back with no dialog shown, and flipped the flag back to
 * `true` — consent inferred from a stale OS grant, not from the person.
 * One flag, "has anyone already decided," closes that gap.
 */
export const PUSH_PERMISSION_ANSWERED_KEY = "duetto_push_permission_answered";

/**
 * device. How many times this device has opened the Today screen, capped
 * at the only threshold anyone reads it against.
 *
 * The daily-push ask waits for the *second* visit: the question is "want
 * this every day?", and it only means anything once the person has seen a
 * day and come back for another. Asking on the first visit spends iOS's
 * one system dialog on a stranger.
 *
 * Device-scoped rather than account-scoped on purpose — it gates a
 * permission dialog that iOS itself scopes to the install.
 */
export const TODAY_VISIT_COUNT_KEY = "duetto_today_visits";

/** cloud. Pointer to the user's latest natal reading (an integer row id). */
export const NATAL_READING_ID_KEY = "duetto_natal_reading_id";

/** cloud. Idempotency key for POST /api/readings/natal (a random UUID). */
export const NATAL_IDEMPOTENCY_KEY = "duetto_natal_idempotency_key";

/** browser localStorage. Admin-only UI preference (auto-refresh toggle). */
export const ADMIN_ECONOMICS_AUTOREFRESH_KEY = "admin.economics.autorefresh";

/**
 * device. Marks that `lib/storageMigration.ts` has already moved the
 * sensitive keys off Telegram CloudStorage for this install, so the app
 * stops paying for cloud round-trips on every launch.
 */
export const STORAGE_MIGRATION_KEY = "duetto_storage_migration";

/**
 * device. DUE-319: set when the visitor taps «Not now» on the `/auth/keep`
 * keep-access gate — suppresses re-asking on this device until the next
 * sign-out/deletion wipes it.
 */
export const KEEP_ACCESS_DISMISSED_KEY = "duetto.keep_access_dismissed";

/**
 * browser localStorage. DUE-318 Important #3: the last `web_surface_enabled`
 * value `WebSurfaceGate` got from `GET /api/config/public`. Read only when
 * that fetch itself fails (network error / non-2xx, not an explicit
 * `false`) so a rate-limited or flaky proxy request doesn't blank the whole
 * web surface — fails open to the cached value, or to the app when there is
 * none yet. Not personal data; deliberately not wiped by `clearClientData`,
 * same as `THEME_PREFERENCE_KEY`.
 */
export const WEB_SURFACE_ENABLED_CACHE_KEY = "duetto_web_surface_enabled_cache";
